import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FolderGit2 } from 'lucide-react';
import { DATA_FR } from '../data/fr';
import { getProjects } from '../services/projectsApi';
import type { Project } from '../services/projectsApi';

export function ProjectsSection({ data }: { data: typeof DATA_FR }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      try {
        const fetched = await getProjects();
        if (fetched && fetched.length > 0) {
          setProjects(fetched);
        } else {
          setProjects(data.projects as any);
        }
      } catch (error) {
        console.error('Failed to load projects', error);
        setProjects(data.projects as any);
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [data.projects]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-full bg-surface-container-lowest rounded-[24px] border border-outline-variant/20 p-6 animate-pulse flex flex-col">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3 w-2/3">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0"></div>
                <div className="h-6 bg-gray-200 rounded w-full"></div>
              </div>
              <div className="w-16 h-6 bg-gray-100 rounded-full"></div>
            </div>
            <div className="space-y-2 mt-4 flex-1">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-100 rounded w-5/6"></div>
              <div className="h-4 bg-gray-100 rounded w-4/6"></div>
            </div>
            <div className="flex gap-2 mt-6">
              <div className="h-6 w-16 bg-gray-200 rounded-lg"></div>
              <div className="h-6 w-16 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {projects.map((project, idx) => {
        const hasLink = !!project.github;
        const Tag = hasLink ? motion.a : motion.div;
        const linkProps = hasLink
          ? { href: project.github, target: '_blank', rel: 'noopener noreferrer' }
          : {};

        return (
          <Tag
            key={project.id}
            {...linkProps}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.15 }}
            className={`group relative flex flex-col h-full bg-surface-container-lowest hover:bg-surface-container-low rounded-[24px] overflow-hidden shadow-sm hover:shadow-elevation-3 transition-all duration-300 border border-outline-variant/20 hover:border-outline-variant/30${hasLink ? ' cursor-pointer' : ' cursor-default'}`}
          >
            <div className="p-6 flex-1 flex flex-col">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-container rounded-full text-on-primary-container group-hover:bg-primary group-hover:text-on-primary transition-colors">
                    <FolderGit2 className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-on-surface group-hover:text-primary transition-colors">
                    {project.name}
                  </h3>
                </div>

                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${project.status === 'En cours' || project.status === 'Ongoing'
                    ? 'bg-tertiary-container text-on-tertiary-container'
                    : 'bg-secondary-container text-on-secondary-container'
                  }`}>
                  {project.status}
                </span>
              </div>

              <p className="text-on-surface-variant text-sm leading-relaxed mb-6 flex-1">
                {project.description}
              </p>

              <div className="flex flex-wrap gap-2 mt-auto">
                {project.tech.map(tech => (
                  <span
                    key={tech}
                    className="px-3 py-1 bg-surface-container-high rounded-lg text-xs font-medium text-on-surface-variant group-hover:bg-surface-container-highest transition-colors"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </Tag>
        );
      })}
    </div>
  );
}
